import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { BATCH_WRITE_SIZE, COL_SALES_TRANSACTIONS, COL_WEEKLY_IMPORTS } from "../constants";
import {
  getCurrentWeekRange,
  getISOWeekNumber,
  isUploadedAtInMonth,
} from "../utils/weekUtils";

export async function createWeeklyImportDraft({
  weekNumber,
  periodKey,
  year,
  monthIndex,
  weekOfMonth,
  fromDate,
  toDate,
  uploadedBy,
  fileName,
  duplicateMode,
  totalRecords,
}) {
  const payload = {
    weekNumber: weekNumber || periodKey || "",
    periodKey: periodKey || weekNumber || "",
    year: year ?? null,
    month: monthIndex != null ? monthIndex + 1 : null,
    weekOfMonth: weekOfMonth ?? null,
    fromDate: fromDate || "",
    toDate: toDate || "",
    uploadedAt: new Date().toISOString(),
    uploadedBy: uploadedBy || "",
    fileName: fileName || "",
    duplicateMode: duplicateMode || "skip",
    status: "in_progress",
    totalRecords: Number(totalRecords) || 0,
    uploadedRecords: 0,
    duplicateRecords: 0,
    failedRecords: 0,
    totalSalesAmount: 0,
  };
  const ref = await addDoc(collection(db, COL_WEEKLY_IMPORTS), payload);
  return { id: ref.id, ...payload };
}

export async function finalizeWeeklyImport(importId, stats) {
  if (!importId) return;
  const ref = doc(db, COL_WEEKLY_IMPORTS, importId);
  const patch = {
    status: stats.status || "completed",
    uploadedAt: new Date().toISOString(),
    uploadedRecords: Number(stats.uploadedRecords) || 0,
    duplicateRecords: Number(stats.duplicateRecords) || 0,
    failedRecords: Number(stats.failedRecords) || 0,
    totalSalesAmount: Number(stats.totalSalesAmount) || 0,
  };
  if (stats.totalRecords != null) patch.totalRecords = Number(stats.totalRecords) || 0;
  await updateDoc(ref, patch);
}

export async function fetchWeeklyImports(max = 100) {
  const q = query(collection(db, COL_WEEKLY_IMPORTS), orderBy("uploadedAt", "desc"), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchLatestWeeklyImport() {
  const list = await fetchWeeklyImports(1);
  return list[0] || null;
}

/**
 * Dashboard aggregates from permanent weekly_imports history.
 */
export async function fetchWeeklyDashboard(selectedMeta) {
  const now = new Date();
  const period =
    selectedMeta?.periodKey ||
    (selectedMeta?.year != null && selectedMeta?.weekOfMonth
      ? `${selectedMeta.year}-${String((selectedMeta.monthIndex ?? now.getMonth()) + 1).padStart(2, "0")}-W${selectedMeta.weekOfMonth}`
      : null);
  const currentWeek = period
    ? { weekNumber: period, fromDate: selectedMeta?.fromDate, toDate: selectedMeta?.toDate }
    : getCurrentWeekRange();
  const imports = await fetchWeeklyImports(250);

  const latest = imports[0] || null;
  const weekImports = imports.filter(
    (i) => i.periodKey === currentWeek.weekNumber || i.weekNumber === currentWeek.weekNumber
  );
  const monthImports = imports.filter((i) => {
    if (selectedMeta?.year != null && selectedMeta?.monthIndex != null) {
      return (
        Number(i.year) === Number(selectedMeta.year) &&
        Number(i.month) === Number(selectedMeta.monthIndex) + 1
      );
    }
    return isUploadedAtInMonth(i.uploadedAt, now);
  });

  const sum = (arr, key) => arr.reduce((s, i) => s + (Number(i[key]) || 0), 0);

  const recordsThisWeek = sum(weekImports, "uploadedRecords");
  const recordsThisMonth = sum(monthImports, "uploadedRecords");
  const salesThisWeek = sum(weekImports, "totalSalesAmount");
  const salesThisMonth = sum(monthImports, "totalSalesAmount");

  const completedThisWeek = weekImports.some(
    (i) => i.status === "completed" && (i.uploadedRecords || 0) > 0
  );
  const inProgress = weekImports.some((i) => i.status === "in_progress");

  let currentWeekStatus = "Not uploaded";
  if (inProgress) currentWeekStatus = "In progress";
  else if (completedThisWeek) currentWeekStatus = "Completed";

  return {
    lastUploadDate: latest?.uploadedAt || null,
    currentWeekStatus,
    recordsThisWeek,
    recordsThisMonth,
    salesThisWeek,
    salesThisMonth,
    currentWeek,
    weekNumber: currentWeek.weekNumber,
  };
}

const DELETE_PAGE = 500;

async function fetchAllMatching(baseConstraints) {
  const docs = [];
  let cursor = null;
  while (true) {
    const parts = [collection(db, COL_SALES_TRANSACTIONS), ...baseConstraints, limit(DELETE_PAGE)];
    if (cursor) parts.push(startAfter(cursor));
    const snap = await getDocs(query(...parts));
    if (snap.empty) break;
    docs.push(...snap.docs);
    if (snap.docs.length < DELETE_PAGE) break;
    cursor = snap.docs[snap.docs.length - 1];
    await new Promise((r) => setTimeout(r, 0));
  }
  return docs;
}

/** Delete in pages without loading the full collection into memory. */
async function deleteMatchingInPages(baseConstraints, onProgress) {
  let deleted = 0;
  while (true) {
    const snap = await getDocs(
      query(collection(db, COL_SALES_TRANSACTIONS), ...baseConstraints, limit(DELETE_PAGE))
    );
    if (snap.empty) break;
    let batch = writeBatch(db);
    let ops = 0;
    for (const d of snap.docs) {
      batch.delete(d.ref);
      ops += 1;
      deleted += 1;
      if (ops >= BATCH_WRITE_SIZE) {
        await batch.commit();
        batch = writeBatch(db);
        ops = 0;
      }
    }
    if (ops > 0) await batch.commit();
    onProgress?.(deleted);
    if (snap.docs.length < DELETE_PAGE) break;
    await new Promise((r) => setTimeout(r, 0));
  }
  return deleted;
}

async function deleteDocRefs(docRefs) {
  let deleted = 0;
  let batch = writeBatch(db);
  let ops = 0;
  for (const ref of docRefs) {
    batch.delete(ref);
    ops += 1;
    deleted += 1;
    if (ops >= BATCH_WRITE_SIZE) {
      await batch.commit();
      batch = writeBatch(db);
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
  return deleted;
}

export async function countTransactionsByImportBatch(importBatchId) {
  if (!importBatchId) return 0;
  const docs = await fetchAllMatching([where("importBatchId", "==", importBatchId)]);
  return docs.length;
}

export async function countTransactionsByWeekNumber(weekNumber) {
  if (!weekNumber) return 0;
  const docs = await fetchAllMatching([where("weekNumber", "==", weekNumber)]);
  return docs.length;
}

export async function countTransactionsByPeriodKey(periodKey) {
  if (!periodKey) return 0;
  const byPeriod = await fetchAllMatching([where("periodKey", "==", periodKey)]);
  if (byPeriod.length) return byPeriod.length;
  return countTransactionsByWeekNumber(periodKey);
}

/** Deletes all sales_transactions for a month-week period (re-upload replaces slice). */
export async function deleteTransactionsByPeriodKey(periodKey, onProgress) {
  if (!periodKey) return 0;
  let n = await deleteMatchingInPages([where("periodKey", "==", periodKey)], onProgress);
  if (n === 0) {
    n = await deleteMatchingInPages([where("weekNumber", "==", periodKey)], onProgress);
  }
  return n;
}

async function deletePeriodExceptBatch(field, periodKey, keepBatchId, onProgress) {
  let deleted = 0;
  let cursor = null;
  while (true) {
    const parts = [
      collection(db, COL_SALES_TRANSACTIONS),
      where(field, "==", periodKey),
      limit(DELETE_PAGE),
    ];
    if (cursor) parts.push(startAfter(cursor));
    const snap = await getDocs(query(...parts));
    if (snap.empty) break;
    const toRemove = snap.docs.filter((d) => d.data()?.importBatchId !== keepBatchId);
    if (toRemove.length) {
      let batch = writeBatch(db);
      let ops = 0;
      for (const d of toRemove) {
        batch.delete(d.ref);
        ops += 1;
        deleted += 1;
        if (ops >= BATCH_WRITE_SIZE) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }
      if (ops > 0) await batch.commit();
      onProgress?.(deleted);
    }
    if (snap.docs.length < DELETE_PAGE) break;
    cursor = snap.docs[snap.docs.length - 1];
    await new Promise((r) => setTimeout(r, 0));
  }
  return deleted;
}

/** After a successful upload, remove prior rows for the same period (other batches). */
export async function deleteTransactionsByPeriodKeyExceptBatch(periodKey, keepBatchId, onProgress) {
  if (!periodKey || !keepBatchId) return 0;
  let deleted = await deletePeriodExceptBatch("periodKey", periodKey, keepBatchId, onProgress);
  deleted += await deletePeriodExceptBatch("weekNumber", periodKey, keepBatchId, onProgress);
  return deleted;
}

/** Deletes only rows from one upload (importBatchId). */
export async function deleteTransactionsByImportBatch(importBatchId) {
  if (!importBatchId) return 0;
  const docs = await fetchAllMatching([where("importBatchId", "==", importBatchId)]);
  return deleteDocRefs(docs.map((d) => d.ref));
}

/** Deletes all sales_transactions tagged with this ISO week (all uploads). */
export async function deleteTransactionsByWeekNumber(weekNumber) {
  if (!weekNumber) return 0;
  const docs = await fetchAllMatching([where("weekNumber", "==", weekNumber)]);
  return deleteDocRefs(docs.map((d) => d.ref));
}

export async function countAllSalesTransactions() {
  let total = 0;
  let cursor = null;
  while (true) {
    const parts = [collection(db, COL_SALES_TRANSACTIONS), limit(DELETE_PAGE)];
    if (cursor) parts.push(startAfter(cursor));
    const snap = await getDocs(query(...parts));
    if (snap.empty) break;
    total += snap.docs.length;
    if (snap.docs.length < DELETE_PAGE) break;
    cursor = snap.docs[snap.docs.length - 1];
  }
  return total;
}

/** Removes every document in sales_transactions (paginated). */
export async function deleteAllSalesTransactions(onProgress) {
  let deleted = 0;
  let cursor = null;
  while (true) {
    const parts = [collection(db, COL_SALES_TRANSACTIONS), limit(DELETE_PAGE)];
    if (cursor) parts.push(startAfter(cursor));
    const snap = await getDocs(query(...parts));
    if (snap.empty) break;
    const n = await deleteDocRefs(snap.docs.map((d) => d.ref));
    deleted += n;
    onProgress?.(deleted);
    if (snap.docs.length < DELETE_PAGE) break;
    cursor = snap.docs[snap.docs.length - 1];
    await new Promise((r) => setTimeout(r, 0));
  }
  return deleted;
}

export async function listWeekNumbersFromImports() {
  const { listUploadedWeekPeriods } = await import("./salesLineChunksReader");
  const periods = await listUploadedWeekPeriods();
  const weeks = periods.map((p) => p.periodKey).filter(Boolean);
  if (weeks.length) return weeks;
  const imports = await fetchWeeklyImports(100);
  const legacy = [...new Set(imports.map((i) => i.periodKey || i.weekNumber).filter(Boolean))];
  return legacy;
}

export function buildWeekMeta(overrides = {}) {
  const base = getCurrentWeekRange();
  return {
    weekNumber: overrides.weekNumber || base.weekNumber,
    fromDate: overrides.fromDate || base.fromDate,
    toDate: overrides.toDate || base.toDate,
  };
}

export { getISOWeekNumber, getCurrentWeekRange };
